import { ComponentProps, FC, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Box } from '@updater/ui-uds';
import {
  PlanCardList,
  PlanCardListItemDiscount,
  PlanCardListItemContractLength,
  PlanCardListItemStarlinkCallouts,
  PlanCardListItemAutoPayDisclaimer,
  PlanCardBroadbandLinkoutItem,
  PlanCardOfferInfo,
  PlanCardListItemInstantOn,
  PlanCardListItemTelevision,
  PlanCardListItemBuildings,
  PlanCardDummyOfferDisclaimer,
  PlanCardListItemAttOffer,
  PlanCardListItemStarryOffer,
  PlanCardListItemStarryEquipmentDetail,
  PlanCardListItemFiberConnection,
} from '@updater/ui-widgets';
import { PlanCardListItemEquipmentRental } from 'components/PlanCard/PlanCardListItemEquipmentRental';

type AccentPlanCardSummaryProps = ComponentProps<typeof Box> & {
  isDesktopGrid?: boolean;
};

const DesktopGridPlanCardList = styled(PlanCardList)<{ $columns: boolean }>`
  ${({ theme }) => theme.mediaQueries.l} {
    display: block !important;
    max-width: 237px;
    margin: 0;
    ${({ $columns, theme }) =>
      $columns &&
      `column-count: 2; column-gap: ${theme.space.m}px; max-width: none;`}
    & > * {
      break-inside: avoid;
      margin-bottom: ${({ theme }) => theme.space.xs}px;
    }
  }
`;

const MIN_HEIGHT_FOR_COLUMNS = 85;

/**
 * `summary` content for `AccentPlanCard` — the card renders its own header
 * (offerTitle, logo, pricing). Two modes, mirroring `PlanCardSummary`'s
 * `isAccentCard`/`isDesktopGrid` split in consumer-app: the reduced 5-item
 * list for the Showcase carousel (default), and the full annotation list
 * in a responsive 1-2 column grid for the main offer list.
 */
export const AccentPlanCardSummary: FC<AccentPlanCardSummaryProps> = ({
  isDesktopGrid = false,
  ...rest
}) => {
  const desktopListRef = useRef<HTMLDivElement>(null);
  const [desktopColumns, setDesktopColumns] = useState(false);

  useLayoutEffect(() => {
    if (!isDesktopGrid) return;
    const el = desktopListRef.current;
    if (!el) return;
    setDesktopColumns(el.scrollHeight >= MIN_HEIGHT_FOR_COLUMNS);
  }, [isDesktopGrid]);

  return (
    <Box {...rest}>
      {isDesktopGrid ? (
        <Box ref={desktopListRef}>
          <DesktopGridPlanCardList $columns={desktopColumns}>
            <PlanCardListItemInstantOn />
            <PlanCardListItemTelevision />
            <PlanCardListItemBuildings />
            <PlanCardListItemDiscount />
            <PlanCardDummyOfferDisclaimer />
            <PlanCardListItemContractLength />
            <PlanCardListItemAttOffer />
            <PlanCardListItemStarryOffer />
            <PlanCardListItemStarryEquipmentDetail />
            <PlanCardListItemStarlinkCallouts />
            <PlanCardListItemEquipmentRental />
            <PlanCardListItemFiberConnection />
            <PlanCardListItemAutoPayDisclaimer />
            <PlanCardBroadbandLinkoutItem />
          </DesktopGridPlanCardList>
        </Box>
      ) : (
        <PlanCardList mb="none">
          <PlanCardListItemDiscount />
          <PlanCardListItemContractLength />
          <PlanCardListItemStarlinkCallouts />
          <PlanCardListItemAutoPayDisclaimer />
          <PlanCardBroadbandLinkoutItem />
        </PlanCardList>
      )}
      <PlanCardOfferInfo />
    </Box>
  );
};
